/**
 * Import/export hub (F3-J): one card per IO entity registered on the server
 * (`GET /api/io/entities`), with xlsx/csv export and the import wizard. Org-scoped
 * entities use the switcher's active organization.
 */

import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { ArrowDownUp, FileSpreadsheet, Upload } from "@fleet/icons";
import { adminRoute } from "./admin";
import { Badge, Card, CardContent, PageContainer, Skeleton } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { api, createApiQuery } from "@/lib/api";
import type { components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { ExportButton } from "@/features/io/components/export-button";
import { ImportWizard } from "@/features/io/components/import-wizard";
import { type IoEntity, ioQueryKeys } from "@/features/io/api";

type ErrorModel = components["schemas"]["ErrorModel"];
type EntitiesResponse = components["schemas"]["IoEntitiesResponse"];

export function ImportExportHub() {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const [importing, setImporting] = createSignal<IoEntity | null>(null);

  const query = createApiQuery<EntitiesResponse, ErrorModel>(
    () => ioQueryKeys.entities(activeOrgId()),
    async () =>
      api.GET("/api/io/entities", {
        params: { query: { orgId: activeOrgId() } },
      }),
    () => ({ staleTime: 30_000 }),
  );

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <ArrowDownUp class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Import / Export`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Move data in and out of the academy as Excel/CSV files. Imports run a dry-run first and are idempotent (re-importing updates instead of duplicating) — this is the path to migrate your existing records.`,
            )}
          </p>
        </div>
      </div>

      <Show when={!query.isLoading} fallback={<Skeleton class="h-40 w-full rounded-xl" />}>
        <div class="grid gap-4 sm:grid-cols-2" data-testid="io-entities-grid">
          <For each={query.data?.data ?? []}>
            {(entity) => (
              <Card class="h-full">
                <CardContent class="p-5 space-y-3">
                  <div class="flex items-start justify-between gap-2">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                        <FileSpreadsheet class="h-5 w-5" />
                      </div>
                      <div class="min-w-0">
                        <div class="font-bold truncate">{entity.nameI18n}</div>
                        <div class="text-xs text-muted-foreground font-mono">{entity.key}</div>
                      </div>
                    </div>
                    <Badge variant="outline">{entity.scope}</Badge>
                  </div>
                  <div class="text-xs text-muted-foreground">
                    {entity.columns
                      .map((c) => (c.required ? `${c.labelI18n}*` : c.labelI18n))
                      .join(" · ")}
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <Show when={entity.exportable}>
                      <ExportButton entityKey={entity.key} orgId={activeOrgId()} />
                    </Show>
                    <Show when={entity.importable}>
                      <Button
                        size="sm"
                        onClick={() => setImporting(entity)}
                        data-testid={`io-import-${entity.key}`}
                      >
                        <Upload class="h-4 w-4" /> {t(msg`Import`)}
                      </Button>
                    </Show>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
        <Show when={(query.data?.data ?? []).length === 0}>
          <Card>
            <CardContent class="p-8 text-center text-sm text-muted-foreground">
              {t(msg`No import/export entities are available for your role.`)}
            </CardContent>
          </Card>
        </Show>
      </Show>

      <Show when={importing()}>
        {(entity) => (
          <ImportWizard
            entity={entity()}
            orgId={activeOrgId()}
            open={importing() !== null}
            onOpenChange={(open) => {
              if (!open) {
                setImporting(null);
              }
            }}
          />
        )}
      </Show>
    </div>
  );
}

function AdminImportExportPage() {
  return (
    <PageContainer maxWidth="4xl">
      <ImportExportHub />
    </PageContainer>
  );
}

export const adminImportExportRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/import-export",
  component: AdminImportExportPage,
});
